import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import L from "leaflet";
import { Pause, Play, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Empty, Field, KV, NSelect, PageHeader } from "@/components/common";
import { useLeafletMap } from "@/hooks/useLeafletMap";
import { drawRoute, vehicleIcon } from "@/components/map/markers";
import { useFleet } from "@/context/FleetContext";
import { api, errMsg } from "@/lib/api";
import { dateInput, fmtDateTime, fmtDuration, toIso } from "@/lib/fleet";
import { cn } from "@/lib/utils";

const SPEEDS = [1, 2, 4, 8];

export function PlaybackView({ fixedVehicleId }) {
  const { vehicles, byId } = useFleet();
  const [params] = useSearchParams();
  const [vid, setVid] = useState(fixedVehicleId || params.get("v") || "");
  const [date, setDate] = useState(dateInput());
  const [t1, setT1] = useState("00:00");
  const [t2, setT2] = useState("23:59");
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const mapRef = useRef(null);
  const map = useLeafletMap(mapRef);
  const routeLayer = useRef(null);
  const mover = useRef(null);

  useEffect(() => {
    if (!vid && vehicles.length) setVid(vehicles[0].id);
  }, [vehicles, vid]);

  const load = async () => {
    setPlaying(false);
    try {
      const { data: d } = await api.get(`/vehicles/${vid}/positions`, { params: { start: toIso(date, t1), end: toIso(date, t2) } });
      setData(d);
      setIdx(0);
      if (!d.positions.length) toast.info("Tidak ada data GPS pada rentang waktu ini");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    if (!map || !data) return undefined;
    routeLayer.current = drawRoute(map, data.positions, { stops: data.stops });
    mapRef.current.classList.add("gt-no-anim");
    if (data.positions.length) {
      const v = byId[vid] || {};
      mover.current = L.marker([data.positions[0].lat, data.positions[0].lng], { icon: vehicleIcon({ ...v, ...data.positions[0], server_time: new Date().toISOString(), speed: 10 }, true, false), zIndexOffset: 2000 }).addTo(map);
    }
    return () => {
      routeLayer.current?.remove();
      mover.current?.remove();
      mover.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, data]);

  const p = data?.positions?.[idx];
  useEffect(() => {
    if (!p || !mover.current) return;
    mover.current.setLatLng([p.lat, p.lng]);
    mover.current.setIcon(vehicleIcon({ ...(byId[vid] || {}), direction: p.direction, speed: p.speed > 3 ? p.speed : 0, acc: p.acc_status, server_time: new Date().toISOString() }, true, false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, data]);

  useEffect(() => {
    if (!playing || !data) return undefined;
    const iv = setInterval(() => {
      setIdx((i) => {
        if (i + 1 >= data.positions.length) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, 320 / speed);
    return () => clearInterval(iv);
  }, [playing, speed, data]);

  const n = data?.positions?.length || 0;
  const s = data?.summary;

  return (
    <div className="space-y-4">
      <div className="gt-card grid grid-cols-2 gap-3 p-4 md:grid-cols-5">
        {!fixedVehicleId && (
          <Field label="Kendaraan" className="col-span-2 md:col-span-1">
            <NSelect value={vid} onChange={setVid} testId="playback-vehicle-select">
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}
            </NSelect>
          </Field>
        )}
        <Field label="Tanggal" className="col-span-2 md:col-span-1"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="gt-input" data-testid="playback-date" /></Field>
        <Field label="Jam mulai"><input type="time" value={t1} onChange={(e) => setT1(e.target.value)} className="gt-input" data-testid="playback-start-time" /></Field>
        <Field label="Jam selesai"><input type="time" value={t2} onChange={(e) => setT2(e.target.value)} className="gt-input" data-testid="playback-end-time" /></Field>
        <div className="col-span-2 flex items-end md:col-span-1">
          <button onClick={load} className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-bold text-primary-foreground hover:opacity-90" data-testid="playback-load-btn"><Search className="h-4 w-4" /> Muat Rute</button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
        <div className="gt-card relative h-[440px] overflow-hidden lg:h-[600px]" data-testid="playback-map">
          <div ref={mapRef} className="absolute inset-0" />
          {!data && <div className="pointer-events-none absolute left-1/2 top-4 z-[500] -translate-x-1/2 rounded-full border border-border bg-card/90 px-3 py-1.5 text-xs font-semibold backdrop-blur">Pilih kendaraan & waktu, lalu muat rute</div>}
          {n > 0 && (
            <div className="absolute inset-x-3 bottom-3 z-[500] rounded-2xl border border-border bg-card/95 p-3 backdrop-blur-xl" data-testid="playback-controls">
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => { if (idx >= n - 1) setIdx(0); setPlaying((x) => !x); }} data-testid="playback-play-btn"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-[1px]" />}
                </button>
                <button onClick={() => { setIdx(0); setPlaying(false); }} className="flex h-10 w-10 items-center justify-center rounded-full border border-border" data-testid="playback-reset-btn"><RotateCcw className="h-4 w-4" /></button>
                <div className="flex rounded-full border border-border p-0.5">
                  {SPEEDS.map((x) => (
                    <button key={x} onClick={() => setSpeed(x)} data-testid={`playback-speed-${x}x`}
                      className={cn("rounded-full px-2.5 py-1 font-mono text-xs font-bold transition-colors", speed === x ? "bg-foreground text-background" : "text-muted-foreground")}>{x}x</button>
                  ))}
                </div>
                <div className="ml-auto text-right">
                  <div className="font-mono text-sm font-bold" data-testid="playback-current-speed">{Math.round(p?.speed || 0)} km/j</div>
                  <div className="font-mono text-[11px] text-muted-foreground" data-testid="playback-current-time">{fmtDateTime(p?.gps_time)}</div>
                </div>
              </div>
              <Slider value={[idx]} max={Math.max(0, n - 1)} step={1} onValueChange={([x]) => setIdx(x)} className="mt-3" data-testid="playback-slider" />
            </div>
          )}
        </div>
        <div className="space-y-3">
          {s ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <KV label="Jarak" value={`${s.distance_km} km`} testId="playback-distance" />
                <KV label="Kec. Maks" value={`${Math.round(s.max_speed)} km/j`} />
                <KV label="Durasi" value={fmtDuration(s.duration_s)} />
                <KV label="Waktu jalan" value={fmtDuration(s.moving_s)} />
                <KV label="Titik GPS" value={s.points} />
                <KV label="Berhenti" value={data.stops.length} />
              </div>
              <div className="gt-card gt-scroll max-h-[340px] overflow-y-auto p-3">
                <div className="mb-2 text-sm font-bold">Stop point</div>
                {data.stops.map((st, i) => (
                  <button key={i} onClick={() => map.flyTo([st.lat, st.lng], 16)} className="mb-1 flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent" data-testid={`playback-stop-${i}`}>
                    <span className="flex items-center gap-2"><span className="gt-pin !h-5 !w-5 !border-2 text-[9px]" style={{ background: "#3B82F6" }}>P</span>{new Date(st.start_time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                    <span className="font-mono text-muted-foreground">{fmtDuration(st.duration_s)}</span>
                  </button>
                ))}
                {!data.stops.length && <div className="text-xs text-muted-foreground">Tidak ada stop &gt; 2 menit</div>}
              </div>
            </>
          ) : (
            <div className="gt-card"><Empty icon={Play} title="Belum ada rute" text="Ringkasan perjalanan akan tampil di sini." /></div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PlaybackPage() {
  return (
    <div>
      <PageHeader eyebrow="Playback" title="Putar Ulang Perjalanan" subtitle="Jalur, marker bergerak, start/end point dan stop point dengan kontrol kecepatan 1x – 8x." />
      <PlaybackView />
    </div>
  );
}
